import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, InfoWindow, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL, API_URL_UPLOADS, GOOGLE_MAPS_API_KEY } from "./config";
import driverCarIcon from "./assets/3d_xgo_honda_fit_car_image.png";
import passengerIcon from "./assets/passanger_icon.png";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const safeNumber = (value) => {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : null;
};

const pickFirst = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
};

const getLatLng = (row) => {
  const lat = safeNumber(
    pickFirst(row, [
      "lat_cordinates",
      "lat_coordinates",
      "lat",
      "latitude",
      "customer_lat",
      "customerLat",
    ])
  );
  const lng = safeNumber(
    pickFirst(row, [
      "long_cordinates",
      "lng_cordinates",
      "long_coordinates",
      "lng",
      "longitude",
      "customer_long",
      "customerLng",
    ])
  );

  if (lat === null || lng === null) return null;
  return { lat, lng };
};

const getDisplayName = (row) => {
  const name = pickFirst(row, ["name", "first_name", "firstname", "username"]);
  const surname = pickFirst(row, ["surname", "last_name", "lastname"]);
  const full = [name, surname].filter(Boolean).join(" ").trim();
  return full || "Unknown";
};

const getUserId = (row) =>
  pickFirst(row, [
    "driver_id",
    "customerid",
    "customer_id",
    "customerId",
    "userid",
    "userId",
    "id",
  ]);

const fetchJsonWithTimeout = async (url, timeoutMs = 15000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status} for ${url}`);
    }
    return await res.json();
  } finally {
    clearTimeout(timeoutId);
  }
};

const CustomersMap = () => {
  const [customersRaw, setCustomersRaw] = useState([]);
  const [driversRaw, setDriversRaw] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedOnceRef = useRef(false);
  const initialFitDoneRef = useRef(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "xgo-customers-map",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const customerMarkerIcon = useMemo(() => {
    const url = passengerIcon;
    if (!isLoaded || !window.google || !window.google.maps) return { url };
    return {
      url,
      scaledSize: new window.google.maps.Size(34, 34),
    };
  }, [isLoaded]);

  const driverMarkerIcon = useMemo(() => {
    const url = driverCarIcon;
    if (!isLoaded || !window.google || !window.google.maps) return { url };
    return {
      url,
      scaledSize: new window.google.maps.Size(42, 42),
    };
  }, [isLoaded]);

  const fitToPoints = (m, points) => {
    if (!m) return;
    if (!window.google || !window.google.maps) return;
    if (!Array.isArray(points) || points.length === 0) return;

    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);

    const boundsLiteral = {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };

    try {
      m.fitBounds(boundsLiteral);
    } catch (e) {
      // Ignore fitBounds errors; markers will still render
    }
  };

  const fetchData = useCallback(async () => {
    setRefreshing(hasLoadedOnceRef.current);
    setError(null);

    try {
      const [customersRes, driversRes] = await Promise.all([
        fetchJsonWithTimeout(`${API_URL}/customerdetails/`),
        fetchJsonWithTimeout(`${API_URL}/driver/`),
      ]);

      setCustomersRaw(Array.isArray(customersRes) ? customersRes : []);
      setDriversRaw(Array.isArray(driversRes) ? driversRes : []);
    } catch (e) {
      // Keep last known good data on errors/timeouts; only surface error text.
      const message =
        e?.name === "AbortError"
          ? "Request timed out while loading customer/driver data."
          : e?.message || "Failed to load customer/driver data.";
      setError(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
      hasLoadedOnceRef.current = true;
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const customers = useMemo(() => {
    return customersRaw
      .map((c) => {
        const pos = getLatLng(c);
        if (!pos) return null;
        // Additional validation to ensure coordinates are valid numbers
        if (!Number.isFinite(pos.lat) || !Number.isFinite(pos.lng)) {
          console.warn("Invalid coordinates for customer:", c.userId, pos);
          return null;
        }
        return {
          kind: "customer",
          raw: c,
          position: pos,
          userId: getUserId(c),
          name: getDisplayName(c),
          profilePic: pickFirst(c, ["profilePic", "profile_pic", "avatar"]),
        };
      })
      .filter(Boolean);
  }, [customersRaw]);

  const drivers = useMemo(() => {
    return driversRaw
      .map((d) => {
        const pos = getLatLng(d);
        if (!pos) return null;
        // Additional validation to ensure coordinates are valid numbers
        if (!Number.isFinite(pos.lat) || !Number.isFinite(pos.lng)) {
          console.warn("Invalid coordinates for driver:", d.userId, pos);
          return null;
        }
        return {
          kind: "driver",
          raw: d,
          position: pos,
          userId: getUserId(d),
          name: getDisplayName(d),
          profilePic: pickFirst(d, ["profilePic", "profile_pic", "avatar"]),
        };
      })
      .filter(Boolean);
  }, [driversRaw]);

  useEffect(() => {
    console.log(
      "[CustomersMap] customers extracted:",
      customers.map((c) => ({
        customerid: c.userId,
        name: c.name,
        profilePic: c.profilePic,
        lat_cordinates: c.position.lat,
        long_cordinates: c.position.lng,
      }))
    );
  }, [customers]);

  useEffect(() => {
    console.log(
      "[CustomersMap] drivers extracted:",
      drivers.map((d) => ({
        driver_id: d.userId,
        name: d.name,
        profilePic: d.profilePic,
        lat_cordinates: d.position.lat,
        long_cordinates: d.position.lng,
      }))
    );
  }, [drivers]);

  const center = useMemo(() => {
    // Find first valid customer position
    const validCustomer = customers.find(c => 
      c.position && 
      Number.isFinite(c.position.lat) && 
      Number.isFinite(c.position.lng)
    );
    if (validCustomer) return validCustomer.position;
    
    // Find first valid driver position
    const validDriver = drivers.find(d => 
      d.position && 
      Number.isFinite(d.position.lat) && 
      Number.isFinite(d.position.lng)
    );
    if (validDriver) return validDriver.position;
    
    // Fallback to default coordinates
    return { lat: -17.8292, lng: 31.0522 };
  }, [customers, drivers]);

  useEffect(() => {
    if (!map) return;
    if (initialFitDoneRef.current) return;
    if (!window.google || !window.google.maps) return;

    const points = [...customers, ...drivers]
      .map((x) => x.position)
      .filter((p) =>
        p &&
        typeof p.lat === "number" &&
        Number.isFinite(p.lat) &&
        typeof p.lng === "number" &&
        Number.isFinite(p.lng)
      );

    if (points.length === 0) return;
    fitToPoints(map, points);
    initialFitDoneRef.current = true;
  }, [map, customers, drivers]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <p className="text-lg text-gray-700">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <p className="text-lg text-red-700">Failed to load Google Maps.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="relative flex flex-col" style={{ height: "100vh" }}>
        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}

        <div className="p-3 bg-white border-b border-gray-200 flex flex-wrap items-center gap-3">
          <div className="text-sm text-gray-700">
            Customers plotted: <strong>{customers.length}</strong> / {customersRaw.length}
          </div>
          <div className="text-sm text-gray-700">
            Drivers plotted: <strong>{drivers.length}</strong> / {driversRaw.length}
          </div>
          <div className="text-xs text-gray-500">{refreshing ? "Refreshing…" : ""}</div>
          <div className="text-xs text-gray-500">
            Only users with non-null <code>lat_cordinates</code> and <code>long_cordinates</code> will appear.
          </div>

          <button
            className="ml-auto px-3 py-1 rounded bg-[#002966] text-white text-sm"
            onClick={() => {
              const points = [...customers, ...drivers]
                .map((x) => x.position)
                .filter((p) =>
                  p &&
                  typeof p.lat === "number" &&
                  Number.isFinite(p.lat) &&
                  typeof p.lng === "number" &&
                  Number.isFinite(p.lng)
                );
              fitToPoints(map, points);
            }}
            type="button"
          >
            Recenter
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          {!isLoaded ? (
            <div className="h-full flex items-center justify-center bg-gray-50">
              <p className="text-gray-700">Loading map...</p>
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              defaultCenter={center}
              defaultZoom={13}
              onLoad={(m) => {
                setMap(m);
                const points = [...customers, ...drivers]
                  .map((x) => x.position)
                  .filter((p) =>
                    p &&
                    typeof p.lat === "number" &&
                    Number.isFinite(p.lat) &&
                    typeof p.lng === "number" &&
                    Number.isFinite(p.lng)
                  );
                fitToPoints(m, points);
              }}
            >
            {customers.filter(c => 
              c.position && 
              Number.isFinite(c.position.lat) && 
              Number.isFinite(c.position.lng)
            ).map((c) => (
              <MarkerF
                key={String(c.userId ?? `${c.position.lat}-${c.position.lng}`)}
                position={c.position}
                zIndex={1000}
                icon={customerMarkerIcon}
                label={
                  c.name
                    ? {
                        text: c.name,
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#b91c1c",
                      }
                    : undefined
                }
                onClick={() => setSelectedEntity(c)}
              />
            ))}

            {drivers.filter(d => 
              d.position && 
              Number.isFinite(d.position.lat) && 
              Number.isFinite(d.position.lng)
            ).map((d) => (
              <MarkerF
                key={String(d.userId ?? `driver-${d.position.lat}-${d.position.lng}`)}
                position={d.position}
                zIndex={900}
                icon={driverMarkerIcon}
                label={
                  d.name
                    ? {
                        text: d.name,
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#1d4ed8",
                      }
                    : undefined
                }
                onClick={() => setSelectedEntity(d)}
              />
            ))}

            {selectedEntity && (
              <InfoWindow
                position={selectedEntity.position}
                onCloseClick={() => setSelectedEntity(null)}
              >
                <div style={{ minWidth: 220 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {selectedEntity.profilePic ? (
                      <img
                        src={`${API_URL_UPLOADS}/${String(selectedEntity.profilePic).replace(
                          /\\\\/g,
                          "/"
                        )}`}
                        alt={selectedEntity.name}
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 999,
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 999,
                          background: "#e5e7eb",
                        }}
                      />
                    )}

                    <div>
                      <div style={{ fontWeight: 700, color: "#002966" }}>
                        {selectedEntity.name}
                      </div>
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        USER ID: {selectedEntity.userId ?? "N/A"}
                      </div>
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        TYPE: {selectedEntity.kind}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 10, fontSize: 12, color: "#374151" }}>
                    <div>Lat: {selectedEntity.position.lat}</div>
                    <div>Lng: {selectedEntity.position.lng}</div>
                  </div>
                </div>
              </InfoWindow>
            )}
            </GoogleMap>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomersMap;
